import { useState, useEffect } from 'react';
import { 
  FaUserCircle, 
  FaLock, 
  FaBell, 
  FaShareAlt,
  FaEye,
  FaEyeSlash,
  FaSave,
  FaClock
} from 'react-icons/fa';
import HttpInterceptor from '../../lib/HttpInterceptor';
import { toast } from 'react-toastify';

function StudentSettings() {
  // Form state
  const [formData, setFormData] = useState({
    profileVisibility: 'public',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: '',
      linkedin: ''
    },
    notifications: {
      email: true,
      push: true,
      classReminders: true,
      courseUpdates: true,
      promotions: false
    },
    security: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    },
    dateTime: {
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12-hour'
    }
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await HttpInterceptor.get('/api/students/settings');
        const { profileVisibility, notifications, socialMedia, dateFormat, timeFormat } = data.data || data;
        setFormData((prev) => ({
          ...prev,
          profileVisibility: profileVisibility || 'public',
          notifications: {
            email: notifications?.email ?? true,
            push: notifications?.push ?? true,
            classReminders: notifications?.classReminders ?? true,
            courseUpdates: notifications?.courseUpdates ?? true,
            promotions: notifications?.promotions ?? false,
          },
          socialMedia: {
            facebook: socialMedia?.facebook || '',
            instagram: socialMedia?.instagram || '',
            twitter: socialMedia?.twitter || '',
            linkedin: socialMedia?.linkedin || '',
          },
          dateTime: {
            dateFormat: dateFormat || 'MM/DD/YYYY',
            timeFormat: timeFormat || '12-hour',
          },
        }));
      } catch (err) {
        console.error('Error fetching settings:', err);
        const errorMsg = err.response?.data?.message || 'Failed to load settings. Please ensure you are logged in.';
        setError(errorMsg);
        toast.error(errorMsg);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (activeTab === 'profile') {
        await HttpInterceptor.put(
          '/api/students/settings/profile',
          { profileVisibility: formData.profileVisibility }
        );
        toast.success('Profile settings saved successfully');
      } else if (activeTab === 'security') {
        // Validate current password is provided
        if (!formData.security.currentPassword || !formData.security.currentPassword.trim()) {
          setError('Current password is required');
          return;
        }
        
        // Validate new password
        if (!formData.security.newPassword || !formData.security.newPassword.trim()) {
          setError('New password is required');
          return;
        }
        
        if (formData.security.newPassword.length < 6) {
          setError('New password must be at least 6 characters long');
          return;
        }
        if (formData.security.newPassword.length > 12) {
          setError('New password must be at most 12 characters long');
          return;
        }
        
        // Password strength validation
        if (!/[a-zA-Z]/.test(formData.security.newPassword)) {
          setError('Password must contain at least one letter');
          return;
        }
        if (!/[0-9]/.test(formData.security.newPassword)) {
          setError('Password must contain at least one number');
          return;
        }
        
        // Validate password match
        if (formData.security.newPassword !== formData.security.confirmPassword) {
          setError('New password and confirm password do not match');
          return;
        }
        
        await HttpInterceptor.put(
          '/api/students/settings/security',
          {
            currentPassword: formData.security.currentPassword,
            newPassword: formData.security.newPassword
          }
        );
        setFormData((prev) => ({
          ...prev,
          security: { currentPassword: '', newPassword: '', confirmPassword: '' },
        }));
        toast.success('Password updated successfully');
      } else if (activeTab === 'notifications') {
        await HttpInterceptor.put(
          '/api/students/settings/notifications',
          { notifications: formData.notifications }
        );
        toast.success('Notification settings saved successfully');
      } else if (activeTab === 'social') {
        // Validate and sanitize social media URLs
        const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
        const socialErrors = {};
        const sanitizedSocialMedia = {};
        
        // Sanitize and validate each URL
        const sanitizeAndValidateURL = (url, platform) => {
          if (!url) return null;
          
          const trimmed = url.trim();
          // Remove any potentially dangerous characters
          const sanitized = trimmed.replace(/[<>"']/g, '');
          
          // Validate URL format
          if (!urlPattern.test(sanitized)) {
            socialErrors[platform] = 'Please enter a valid URL';
            return null;
          }
          
          // Limit URL length
          if (sanitized.length > 500) {
            socialErrors[platform] = 'URL is too long (maximum 500 characters)';
            return null;
          }
          
          return sanitized;
        };
        
        sanitizedSocialMedia.facebook = sanitizeAndValidateURL(formData.socialMedia.facebook, 'facebook');
        sanitizedSocialMedia.instagram = sanitizeAndValidateURL(formData.socialMedia.instagram, 'instagram');
        sanitizedSocialMedia.twitter = sanitizeAndValidateURL(formData.socialMedia.twitter, 'twitter');
        sanitizedSocialMedia.linkedin = sanitizeAndValidateURL(formData.socialMedia.linkedin, 'linkedin');
        
        if (Object.keys(socialErrors).length > 0) {
          setError(`Invalid URLs: ${Object.keys(socialErrors).join(', ')}`);
          return;
        }
        
        await HttpInterceptor.put(
          '/api/students/settings/social-media',
          { socialMedia: sanitizedSocialMedia }
        );
        toast.success('Social media settings saved successfully');
      } else if (activeTab === 'datetime') {
        // Validate date/time format values
        const validDateFormats = ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'];
        const validTimeFormats = ['12-hour', '24-hour'];
        
        if (!validDateFormats.includes(formData.dateTime.dateFormat)) {
          setError('Invalid date format selected');
          return;
        }
        if (!validTimeFormats.includes(formData.dateTime.timeFormat)) {
          setError('Invalid time format selected');
          return;
        }
        
        await HttpInterceptor.put(
          '/api/students/settings/datetime',
          formData.dateTime
        );
        toast.success('Date and time settings saved successfully');
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      const errorMsg = err.response?.data?.message || 'Failed to save settings. Please ensure you are logged in and the server is running.';
      setError(errorMsg);
      toast.error(errorMsg);
    }
  };

  const formatDate = (date) => {
    const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
    switch (formData.dateTime.dateFormat) {
      case 'MM/DD/YYYY':
        return new Intl.DateTimeFormat('en-US', options).format(date);
      case 'DD/MM/YYYY':
        return new Intl.DateTimeFormat('en-GB', options).format(date);
      case 'YYYY-MM-DD':
        return new Intl.DateTimeFormat('sv-SE', options).format(date);
      default:
        return date.toISOString().split('T')[0];
    }
  };

  const formatTime = (date) => {
    const options = { hour: '2-digit', minute: '2-digit', second: '2-digit' };
    if (formData.dateTime.timeFormat === '12-hour') {
      options.hour12 = true;
    } else {
      options.hour12 = false;
    }
    return new Intl.DateTimeFormat('en-US', options).format(date);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">Manage your account preferences and privacy settings</p>
        </div>
      </div>

      {error && <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-red-500">{error}</div>}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm p-4 sticky top-8">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveTab('profile')}
                className={`w-full flex items-center px-4 py-3 rounded-lg ${activeTab === 'profile' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <FaUserCircle className="mr-3" />
                Profile Settings
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`w-full flex items-center px-4 py-3 rounded-lg ${activeTab === 'security' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <FaLock className="mr-3" />
                Security
              </button>
              <button
                onClick={() => setActiveTab('notifications')}
                className={`w-full flex items-center px-4 py-3 rounded-lg ${activeTab === 'notifications' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <FaBell className="mr-3" />
                Notifications
              </button>
              <button
                onClick={() => setActiveTab('social')}
                className={`w-full flex items-center px-4 py-3 rounded-lg ${activeTab === 'social' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <FaShareAlt className="mr-3" />
                Social Media
              </button>
              <button
                onClick={() => setActiveTab('datetime')}
                className={`w-full flex items-center px-4 py-3 rounded-lg ${activeTab === 'datetime' ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                <FaClock className="mr-3" />
                Date & Time
              </button>
            </nav>
          </div>
        </div>

        {/* Main Settings Content */}
        <div className="lg:col-span-3 space-y-8">
          {/* Profile Settings */}
          {activeTab === 'profile' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 id="profile-settings-heading" className="text-2xl font-bold text-gray-800 mb-6">Profile Settings</h2>
              
              <div className="space-y-6">
                {/* Profile Visibility */}
                <div>
                  <label htmlFor="profile-visibility" className="block text-sm font-medium text-gray-700 mb-2">Member Profile Visibility</label>
                  <select
                    id="profile-visibility"
                    name="profileVisibility"
                    value={formData.profileVisibility}
                    onChange={handleChange}
                    className="w-full max-w-md rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-2 px-3"
                  >
                    <option value="public">Public</option>
                    <option value="students">Only Members</option>
                    <option value="trainers">Only Instructors</option>
                    <option value="private">Private</option>
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    Control who can see your name and image in enrollment lists. Enrollment counts always include all students (even those with private profiles) to help trainers showcase program popularity. Your personal details (email, phone) are never shared.
                  </p>
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-blue-800 font-medium mb-1">What each option means:</p>
                    <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                      <li><strong>Public:</strong> Everyone can see your name/image in enrollment lists</li>
                      <li><strong>Only Members:</strong> Other students can see your name/image</li>
                      <li><strong>Only Instructors:</strong> Only trainers can see your name/image</li>
                      <li><strong>Private:</strong> Your enrollment is counted, but your name/image are hidden</li>
                    </ul>
                    <p className="text-xs text-blue-700 mt-2">
                      <strong>Note:</strong> Even with a private profile, you're still counted in enrollment numbers to help trainers showcase their program popularity.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 id="security-settings-heading" className="text-2xl font-bold text-gray-800 mb-6">Security Settings</h2>
              {error && <div className="text-red-500 mb-4">{error}</div>}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      id="current-password"
                      name="security.currentPassword"
                      value={formData.security.currentPassword}
                      onChange={handleChange}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                      aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                    >
                      {showCurrentPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      id="new-password"
                      name="security.newPassword"
                      value={formData.security.newPassword}
                      onChange={handleChange}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showNewPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirm-password"
                      name="security.confirmPassword"
                      value={formData.security.confirmPassword}
                      onChange={handleChange}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 pr-10"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <FaEyeSlash className="h-5 w-5" /> : <FaEye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FaSave className="mr-2" />
                    Update Password
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 id="notification-settings-heading" className="text-2xl font-bold text-gray-800 mb-6">Notification Preferences</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="email-notifications"
                      name="notifications.email"
                      type="checkbox"
                      checked={formData.notifications.email}
                      onChange={handleChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="email-notifications" className="font-medium text-gray-700">
                      Email Notifications
                    </label>
                    <p className="text-gray-500">Receive important updates via email</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="push-notifications"
                      name="notifications.push"
                      type="checkbox"
                      checked={formData.notifications.push}
                      onChange={handleChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="push-notifications" className="font-medium text-gray-700">
                      Push Notifications
                    </label>
                    <p className="text-gray-500">Get instant alerts on your device</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="class-reminders"
                      name="notifications.classReminders"
                      type="checkbox"
                      checked={formData.notifications.classReminders}
                      onChange={handleChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="class-reminders" className="font-medium text-gray-700">
                      Class Reminders
                    </label>
                    <p className="text-gray-500">Reminders for upcoming Sessions</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="course-updates"
                      name="notifications.courseUpdates"
                      type="checkbox"
                      checked={formData.notifications.courseUpdates}
                      onChange={handleChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="course-updates" className="font-medium text-gray-700">
                      Service Updates
                    </label>
                    <p className="text-gray-500">Updates about your enrolled Services</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="promotions"
                      name="notifications.promotions"
                      type="checkbox"
                      checked={formData.notifications.promotions}
                      onChange={handleChange}
                      className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="promotions" className="font-medium text-gray-700">
                      Promotional Communications
                    </label>
                    <p className="text-gray-500">Special offers and news from Yogvital</p>
                  </div>
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FaSave className="mr-2" />
                    Save Preferences
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Social Media Settings */}
          {activeTab === 'social' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 id="social-settings-heading" className="text-2xl font-bold text-gray-800 mb-6">Social Media Links</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="facebook-link" className="block text-sm font-medium text-gray-700 mb-1">
                    Facebook
                  </label>
                  <input
                    type="url"
                    id="facebook-link"
                    name="socialMedia.facebook"
                    value={formData.socialMedia.facebook}
                    onChange={handleChange}
                    placeholder="https://facebook.com/yourprofile"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="instagram-link" className="block text-sm font-medium text-gray-700 mb-1">
                    Instagram
                  </label>
                  <input
                    type="url"
                    id="instagram-link"
                    name="socialMedia.instagram"
                    value={formData.socialMedia.instagram}
                    onChange={handleChange}
                    placeholder="https://instagram.com/yourprofile"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="twitter-link" className="block text-sm font-medium text-gray-700 mb-1">
                    Twitter
                  </label>
                  <input
                    type="url"
                    id="twitter-link"
                    name="socialMedia.twitter"
                    value={formData.socialMedia.twitter}
                    onChange={handleChange}
                    placeholder="https://twitter.com/yourprofile"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label htmlFor="linkedin-link" className="block text-sm font-medium text-gray-700 mb-1">
                    LinkedIn
                  </label>
                  <input
                    type="url"
                    id="linkedin-link"
                    name="socialMedia.linkedin"
                    value={formData.socialMedia.linkedin}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/yourprofile"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                
                <div className="pt-4">
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FaSave className="mr-2" />
                    Save Social Links
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Date & Time Settings */}
          {activeTab === 'datetime' && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 id="datetime-settings-heading" className="text-2xl font-bold text-gray-800 mb-6">Date & Time Settings</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="date-format" className="block text-sm font-medium text-gray-700 mb-2">
                    Date Format
                  </label>
                  <select
                    id="date-format"
                    name="dateTime.dateFormat"
                    value={formData.dateTime.dateFormat}
                    onChange={handleChange}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2 px-3"
                  >
                    <option value="MM/DD/YYYY">MM/DD/YYYY (e.g., 08/02/2025)</option>
                    <option value="DD/MM/YYYY">DD/MM/YYYY (e.g., 02/08/2025)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (e.g., 2025-08-02)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="time-format" className="block text-sm font-medium text-gray-700 mb-2">
                    Time Format
                  </label>
                  <select
                    id="time-format"
                    name="dateTime.timeFormat"
                    value={formData.dateTime.timeFormat}
                    onChange={handleChange}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 py-2 px-3"
                  >
                    <option value="12-hour">12-Hour (e.g., 3:20 PM)</option>
                    <option value="24-hour">24-Hour (e.g., 15:20)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
                  <div className="text-gray-600">
                    Current Date: {formatDate(new Date())}
                    <br />
                    Current Time: {formatTime(new Date())}
                  </div>
                </div>

                <div className="pt-4">
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <FaSave className="mr-2" />
                    Save Date & Time Settings
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentSettings;